import { useState } from "react";
import { Plus, Search, Wrench, AlertTriangle, Clock, CheckCircle2, Filter, Trash2 } from "lucide-react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import {
  useMaintenanceRequests, useCreateMaintenanceRequest, useUpdateMaintenanceRequest, useDeleteMaintenanceRequest,
  PRIORITY_CONFIG, STATUS_CONFIG,
  type MaintenanceStatus, type MaintenancePriority, type MaintenanceCategory, type MaintenanceRequest,
} from "@/hooks/useMaintenance";
import { useUnits } from "@/hooks/useUnits";
import { useAuth } from "@/contexts/AuthContext";

const CATEGORIES: { value: MaintenanceCategory; label: string }[] = [
  { value: "plumbing",     label: "Plumbing" },
  { value: "electrical",   label: "Electrical" },
  { value: "hvac",         label: "HVAC" },
  { value: "elevator",     label: "Elevator" },
  { value: "exterior",     label: "Exterior" },
  { value: "common_area",  label: "Common Area" },
  { value: "unit",         label: "Unit Interior" },
  { value: "general",      label: "General" },
  { value: "other",        label: "Other" },
];

function PriorityBadge({ priority }: { priority: MaintenancePriority }) {
  const cfg = PRIORITY_CONFIG[priority];
  return <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>;
}

function StatusBadge({ status }: { status: MaintenanceStatus }) {
  const cfg = STATUS_CONFIG[status];
  return <span className={`text-xs font-medium ${cfg.color}`}>{cfg.label}</span>;
}

function RequestCard({
  req, onUpdate, onDelete, canDelete,
}: {
  req: MaintenanceRequest;
  onUpdate: (updates: Partial<MaintenanceRequest> & { id: string }) => void;
  onDelete: (id: string) => void;
  canDelete: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  return (
    <Card className="cursor-pointer hover:shadow-sm transition-shadow" onClick={() => setExpanded(!expanded)}>
      <CardContent className="py-3 px-4">
        <div className="flex items-start gap-3">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted mt-0.5">
            <Wrench className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold truncate">{req.title}</span>
              <PriorityBadge priority={req.priority} />
            </div>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <StatusBadge status={req.status} />
              {req.unit?.unit_number && (
                <span className="text-xs text-muted-foreground">Unit {req.unit.unit_number}</span>
              )}
              <span className="text-xs text-muted-foreground">
                · {formatDistanceToNow(parseISO(req.created_at), { addSuffix: true })}
              </span>
              <span className="text-xs text-muted-foreground capitalize">[{req.category}]</span>
            </div>
          </div>
        </div>

        {expanded && (
          <div className="mt-3 space-y-3 pl-11" onClick={e => e.stopPropagation()}>
            <p className="text-sm text-muted-foreground">{req.description}</p>
            {req.submitter?.full_name && (
              <p className="text-xs text-muted-foreground">Submitted by: {req.submitter.full_name}</p>
            )}
            {req.resolution_note && (
              <p className="text-xs bg-green-50 rounded p-2 text-green-800">{req.resolution_note}</p>
            )}
            <Separator />
            <div className="flex flex-wrap gap-2">
              {req.status !== "in_progress" && req.status !== "completed" && req.status !== "cancelled" && (
                <Button size="sm" variant="outline" className="h-7 text-xs"
                  onClick={() => onUpdate({ id: req.id, status: "in_progress" })}>
                  Start Work
                </Button>
              )}
              {req.status === "in_progress" && (
                <Button size="sm" variant="outline" className="h-7 text-xs"
                  onClick={() => onUpdate({ id: req.id, status: "completed" })}>
                  <CheckCircle2 className="mr-1 h-3 w-3" /> Mark Complete
                </Button>
              )}
              {req.status !== "cancelled" && req.status !== "completed" && (
                <Button size="sm" variant="ghost" className="h-7 text-xs text-muted-foreground"
                  onClick={() => onUpdate({ id: req.id, status: "cancelled" })}>
                  Cancel
                </Button>
              )}
              {canDelete && (
                confirmDelete ? (
                  <div className="flex items-center gap-2 ml-auto">
                    <span className="text-xs text-destructive">Delete permanently?</span>
                    <Button size="sm" variant="destructive" className="h-7 text-xs"
                      onClick={() => { onDelete(req.id); setConfirmDelete(false); }}>
                      Yes, delete
                    </Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs"
                      onClick={() => setConfirmDelete(false)}>
                      No
                    </Button>
                  </div>
                ) : (
                  <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive hover:text-destructive ml-auto"
                    onClick={() => setConfirmDelete(true)}>
                    <Trash2 className="mr-1 h-3 w-3" /> Delete
                  </Button>
                )
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function Maintenance() {
  const { user, activeOrgId } = useAuth();
  const [statusFilter, setStatusFilter] = useState<MaintenanceStatus | "all">("all");
  const [search, setSearch] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", category: "general" as MaintenanceCategory,
    priority: "medium" as MaintenancePriority, unit_id: "", due_date: "", estimated_cost: "",
  });

  const { data: requests = [], isLoading } = useMaintenanceRequests(statusFilter);
  const { data: units = [] } = useUnits();
  const createReq = useCreateMaintenanceRequest();
  const updateReq = useUpdateMaintenanceRequest();
  const deleteReq = useDeleteMaintenanceRequest();

  const filtered = requests.filter(r =>
    r.title.toLowerCase().includes(search.toLowerCase()) ||
    r.description.toLowerCase().includes(search.toLowerCase())
  );

  const openCount = requests.filter(r => r.status === "open").length;
  const urgentCount = requests.filter(r => r.priority === "urgent" && r.status !== "completed" && r.status !== "cancelled").length;
  const inProgressCount = requests.filter(r => r.status === "in_progress").length;

  async function handleSubmit() {
    await createReq.mutateAsync({
      title: form.title,
      description: form.description,
      category: form.category,
      priority: form.priority,
      unit_id: form.unit_id || undefined,
      due_date: form.due_date || undefined,
      estimated_cost: form.estimated_cost ? parseFloat(form.estimated_cost) : undefined,
    });
    setShowDialog(false);
    setForm({ title: "", description: "", category: "general", priority: "medium", unit_id: "", due_date: "", estimated_cost: "" });
  }

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Maintenance</h1>
          <p className="text-sm text-muted-foreground">Track and manage building maintenance requests</p>
        </div>
        <Button size="sm" onClick={() => setShowDialog(true)}>
          <Plus className="mr-1.5 h-3.5 w-3.5" /> New Request
        </Button>
      </div>

      {/* ── Summary ── */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border bg-amber-50 border-amber-200 p-3 text-center">
          <p className="text-xl font-bold text-amber-700">{openCount}</p>
          <p className="text-xs text-amber-600">Open</p>
        </div>
        <div className={`rounded-lg border p-3 text-center ${urgentCount > 0 ? "bg-red-50 border-red-200" : "bg-muted border-border"}`}>
          <p className={`text-xl font-bold ${urgentCount > 0 ? "text-red-700" : "text-muted-foreground"}`}>{urgentCount}</p>
          <p className="text-xs text-muted-foreground">Urgent</p>
        </div>
        <div className="rounded-lg border bg-blue-50 border-blue-200 p-3 text-center">
          <p className="text-xl font-bold text-blue-700">{inProgressCount}</p>
          <p className="text-xs text-blue-600">In Progress</p>
        </div>
      </div>

      {/* ── Filters ── */}
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
          <Input className="pl-8 h-8 text-xs" placeholder="Search requests…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Select value={statusFilter} onValueChange={v => setStatusFilter(v as any)}>
          <SelectTrigger className="h-8 w-[140px] text-xs">
            <Filter className="mr-1.5 h-3 w-3" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" className="text-xs">All Status</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <SelectItem key={k} value={k} className="text-xs">{v.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* ── List ── */}
      {isLoading ? (
        <div className="space-y-3">
          {[0,1,2].map(i => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-12">
            <Wrench className="h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              {search ? "No requests matching your search." : "No maintenance requests yet."}
            </p>
            <Button size="sm" variant="outline" onClick={() => setShowDialog(true)}>
              <Plus className="mr-1.5 h-3.5 w-3.5" /> Submit Request
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(req => (
            <RequestCard
              key={req.id}
              req={req}
              onUpdate={(updates) => updateReq.mutate(updates)}
              onDelete={(id) => deleteReq.mutate(id)}
              canDelete={
                req.submitted_by === user?.id ||
                user?.role === "strata_admin" ||
                user?.role === "system_admin"
              }
            />
          ))}
        </div>
      )}

      {/* ── New request dialog ── */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Submit Maintenance Request</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs">Title *</Label>
              <Input className="mt-1" placeholder="Brief description of the issue"
                value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs">Description *</Label>
              <Textarea className="mt-1" rows={3} placeholder="Detailed description…"
                value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v as MaintenanceCategory }))}>
                  <SelectTrigger className="mt-1 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value} className="text-xs">{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Priority</Label>
                <Select value={form.priority} onValueChange={v => setForm(p => ({ ...p, priority: v as MaintenancePriority }))}>
                  <SelectTrigger className="mt-1 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
                      <SelectItem key={k} value={k} className="text-xs">{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Unit (optional)</Label>
                <Select value={form.unit_id || "none"} onValueChange={v => setForm(p => ({ ...p, unit_id: v === "none" ? "" : v }))}>
                  <SelectTrigger className="mt-1 text-xs"><SelectValue placeholder="Select unit" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" className="text-xs">— None —</SelectItem>
                    {(units as any[]).map((u: any) => (
                      <SelectItem key={u.id} value={u.id} className="text-xs">Unit {u.unit_number}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Due Date (optional)</Label>
                <Input type="date" className="mt-1"
                  value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Estimated Cost ($)</Label>
              <Input type="number" className="mt-1" placeholder="0.00"
                value={form.estimated_cost} onChange={e => setForm(p => ({ ...p, estimated_cost: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={createReq.isPending || !form.title || !form.description}>
              {createReq.isPending ? "Submitting…" : "Submit Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
